import * as React from 'react';
import { FC, useState, useEffect, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import fetchAPI from 'app/helpers/fetch';

export interface Meta {
  page: number;
  per: number;
  total_pages: number;
  total_count: number;
}

export interface PaginatedItems<T> {
  data: T[];
  meta: Meta;
}

const moreGamesPresent = (meta: Meta) => !!meta.total_pages && meta.page !== meta.total_pages;

const searchParams = (page: number) => {
  const params = new URLSearchParams(window.location.search);
  params.set('page', (page + 1).toString());
  return params.toString();
};

const useInfiniteScroll: <T>(initialItems?: PaginatedItems<T>) => [() => void, boolean, boolean, T[]] = (
  initialItems
) => {
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState(initialItems?.data || []);
  const [meta, setMeta] = useState(initialItems?.meta);
  const [error, setError] = useState(false);
  const hasMore = !meta || moreGamesPresent(meta);

  const load = () => {
    if (loading || !hasMore) return;

    setLoading(true);
    fetchAPI(`${window.location.pathname}.json?${searchParams(meta ? meta.page : 0)}`)
      .then((newGames) => {
        setGames((g) => [...g, ...newGames.data]);
        setMeta(newGames.meta);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
      });
  };

  return [load, hasMore, error, games];
};

const Error: FC = () => (
  <ul className="alerts error">
    <li className="alert alert-error">Games loading error</li>
  </ul>
);

const ViewTrigger: FC<{ onView: () => void }> = ({ onView }) => {
  const [ref, inView] = useInView({ rootMargin: '0% 0% 50% 0%' });

  useEffect(() => {
    if (inView) {
      onView();
    }
  }, [inView]);

  return <div style={{ padding: 0, margin: 0 }} ref={ref} />;
};

interface InfiniteScrollProps<T> {
  initialItems: PaginatedItems<T>;
  children: (items: T[]) => ReactNode;
}

const InfiniteScroll = <T,>({ initialItems, children }: InfiniteScrollProps<T>): JSX.Element => {
  const [load, hasMore, error, items] = useInfiniteScroll(initialItems);

  return (
    <>
      {children(items)}
      {!error && hasMore && (
        <>
          <ViewTrigger onView={load} />
          <div className="spinner" aria-label="spinner" />
        </>
      )}
      {error && <Error />}
    </>
  );
};

export default InfiniteScroll;
